import React, { useContext, useRef } from "react";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import {
  DateRangePicker,
  LocalizationProvider,
  SingleInputDateRangeField,
  DateRange,
} from "@mui/x-date-pickers-pro";
import { AdapterDateFns } from "@mui/x-date-pickers-pro/AdapterDateFns";
import { pl } from "date-fns/locale";
import { format } from "date-fns";
import __ from "src/utils/Translations";
import useIsAhr from "src/utils/hooks/useIsAhr";
import { useCompanyId, useCompanyName } from "src/Pages/Company/CompanyContext";
import { getCompanyBaseBreadcrumbs } from "src/Pages/Company/routerHelper";
import BreadcrumbsMui from "src/Components/BreadcrumbsMui/BreadcrumbsMui";
import ImportExportLayout from "src/Pages/Company/OrganizationStructure/Layouts/ImportExportLayout";
import { GlobalModalContext } from "src/utils/GlobalModal/GlobalModalContext";
import { useFetchStructureData } from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/OrganizationStructure/api/useFetchStructureData";
import useDownloadReport, {
  ReportContentType,
} from "src/Pages/Company/EmployeeManagement/Reports/useDownloadReport";

interface ReportConfig {
  id: string;
  title: string;
  description: string;
}

const REPORTS: ReportConfig[] = [
  {
    id: "employed",
    title: "Raport zatrudnionych",
    description:
      "Zestawienie aktualnie zatrudnionych pracowników w wybranym okresie.",
  },
  {
    id: "incoming",
    title: "Raport przychodzących",
    description:
      "Zestawienie nowych pracowników zatrudnionych w wybranym okresie.",
  },
  {
    id: "leaving",
    title: "Raport odchodzących",
    description:
      "Zestawienie pracowników, których umowy zakończyły lub zakończą się w wybranym okresie.",
  },
];

interface ReportFormRefs {
  dateRange: DateRange<Date>;
  fileFormat: "csv" | "xlsx";
}

const isDateRangeValid = (dateRange: DateRange<Date>): boolean =>
  dateRange[0] instanceof Date &&
  !Number.isNaN(dateRange[0].getTime()) &&
  dateRange[1] instanceof Date &&
  !Number.isNaN(dateRange[1].getTime());

const ReportModalForm = ({
  onChange,
}: {
  onChange: (data: ReportFormRefs) => void;
}) => {
  const [dateRange, setDateRange] = React.useState<DateRange<Date>>([
    new Date(),
    new Date(),
  ]);
  const [fileFormat, setFileFormat] = React.useState<"csv" | "xlsx">("csv");

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pl}>
      <Box sx={{ mt: 2 }}>
        <FormLabel sx={{ fontWeight: "bold", mb: 1, display: "block" }}>
          {__("Zakres dat")}
        </FormLabel>
        <DateRangePicker
          value={dateRange}
          onChange={(newValue) => {
            setDateRange(newValue);
            onChange({ dateRange: newValue, fileFormat });
          }}
          slots={{ field: SingleInputDateRangeField }}
          slotProps={{
            textField: {
              size: "small",
              fullWidth: true,
              label: __("Wybierz datę"),
              InputLabelProps: { shrink: true },
              inputProps: {
                "data-testid": "os__reports-modal-date-range",
              } as React.InputHTMLAttributes<HTMLInputElement>,
            },
          }}
        />

        <FormControl sx={{ mt: 3 }}>
          <FormLabel sx={{ fontWeight: "bold", mb: 1 }}>
            {__("Format pliku")}
          </FormLabel>
          <RadioGroup
            value={fileFormat}
            onChange={(e) => {
              const val = e.target.value as "csv" | "xlsx";
              setFileFormat(val);
              onChange({ dateRange, fileFormat: val });
            }}
          >
            <FormControlLabel
              value="csv"
              control={<Radio data-testid="os__reports-modal-format-csv" />}
              label="CSV (UTF-8, rozdzielone średnikami)"
            />
            <FormControlLabel
              value="xlsx"
              control={<Radio data-testid="os__reports-modal-format-xlsx" />}
              label="XLSX"
            />
          </RadioGroup>
        </FormControl>
      </Box>
    </LocalizationProvider>
  );
};

const Reports = () => {
  const companyId = useCompanyId();
  const isAhr = useIsAhr();
  const companyName = useCompanyName();
  const { setModal, resetModal } = useContext(GlobalModalContext);
  const { data: structureData } = useFetchStructureData(companyId);
  const structureId: string = structureData?.[0]?.structureId ?? "";
  const { mutate: downloadReport } = useDownloadReport();
  const formRefs = useRef<ReportFormRefs>({
    dateRange: [new Date(), new Date()],
    fileFormat: "csv",
  });

  const baseBreadcrumbs = (
    getCompanyBaseBreadcrumbs(companyId, companyName) as {
      title: string;
      link: string;
    }[]
  ).map(({ title, link }) => ({ label: title, to: link }));

  const breadcrumbs = isAhr
    ? [
        { label: __("Zarządzanie pracownikami"), to: "/ahr" },
        { label: __("Raporty"), to: "/ahr/reports" },
      ]
    : [
        ...baseBreadcrumbs,
        {
          label: __("Zarządzanie pracownikami"),
          to: `/company/edit/${companyId}/employee-management`,
        },
        {
          label: __("Raporty"),
          to: `/company/edit/${companyId}/employee-management/reports`,
        },
      ];

  const openReportModal = (report: ReportConfig) => {
    formRefs.current = {
      dateRange: [new Date(), new Date()],
      fileFormat: "csv",
    };

    const handleChange = (data: ReportFormRefs) => {
      formRefs.current = data;
      setModal((prev) => ({
        ...prev,
        actions:
          prev.actions?.map((action, i) =>
            i === 1
              ? { ...action, disabled: !isDateRangeValid(data.dateRange) }
              : action
          ) ?? [],
      }));
    };

    setModal({
      open: true,
      maxWidth: 628,
      title: __(report.title),
      description: __(report.description),
      disableExitButton: true,
      disableOutsideClick: true,
      customElements: <ReportModalForm onChange={handleChange} />,
      actions: [
        {
          label: __("Anuluj"),
          variant: "outlined" as const,
          callbackFn: () => resetModal(),
        },
        {
          label: __("Generuj raport"),
          variant: "contained" as const,
          callbackFn: () => {
            const { dateRange, fileFormat } = formRefs.current;
            if (dateRange[0] && dateRange[1]) {
              downloadReport({
                companyId,
                structureId,
                contentType:
                  fileFormat === "csv"
                    ? ReportContentType.CSV
                    : ReportContentType.XLSX,
                from: format(dateRange[0], "yyyy-MM-dd"),
                to: format(dateRange[1], "yyyy-MM-dd"),
                reportId: report.id,
              });
            }
            resetModal();
          },
        },
      ],
    });
  };

  return (
    <>
      <BreadcrumbsMui items={breadcrumbs} />
      <ImportExportLayout>
        <Box sx={{ mt: 2 }} />
        <ImportExportLayout.Header
          title={__("Raporty")}
          description={__(
            "Generuj i pobieraj raporty dotyczące pracowników oraz zmian kadrowych."
          )}
          titleSx={{ textTransform: "none" }}
          descriptionSx={{ mt: 1 }}
        />
        <ImportExportLayout.SectionHeader title={__("Raporty kadrowe")} />
        {REPORTS.map((report) => (
          <ImportExportLayout.ActionCard
            key={report.id}
            title={__(report.title)}
            description={__(report.description)}
            actions={
              <Button
                variant="outlined"
                data-testid={`os__reports-generate-${report.id}-button`}
                onClick={() => openReportModal(report)}
              >
                {__("Generuj raport")}
              </Button>
            }
          />
        ))}
      </ImportExportLayout>
    </>
  );
};

export default Reports;
